import {
  QBSLevelItemSelection,
  QBSLevelItemSelectionProvider,
} from '@exam-management/qconfig-add-qbank-to-qconfig';
import { Button, DialogActions, DialogContent, Divider, Flex, Form } from '@shared/components';
import { zodResolver } from '@shared/utils';
import { useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';

import { useAddQBankStructureToQConfigGenerator } from '../../providers';
import { SearchQBSSelection } from '../search-qbs-selection';

const AddQBankToQConfigGeneratorFormSchema = z.object({
  qbankSelection: z.object({
    value: z.string(),
    label: z.string(),
  }),
  levelItems: z.record(z.string(), z.boolean()),
});

export interface AddQBankToQConfigGeneratorFormProps {
  onClose: () => void;
  setSelectedQBankStructure: (value: AddQBankToQConfigGeneratorFormValues | null) => void;
  addedQBSIds: string[];
}

export type AddQBankToQConfigGeneratorFormValues = z.infer<
  typeof AddQBankToQConfigGeneratorFormSchema
>;

export default function AddQBankToQConfigGeneratorForm(
  props: Readonly<AddQBankToQConfigGeneratorFormProps>,
) {
  const { onClose, setSelectedQBankStructure, addedQBSIds } = props;
  const formContext = useForm<AddQBankToQConfigGeneratorFormValues>({
    defaultValues: {
      qbankSelection: {
        value: '',
        label: '',
      },
      levelItems: {},
    },
    resolver: zodResolver(AddQBankToQConfigGeneratorFormSchema),
  });

  const qBankSelection = formContext.watch('qbankSelection')?.value;
  const { qBankStructureLevelItems, qBankStructureLevels, setQbankStructureId, isLoading } =
    useAddQBankStructureToQConfigGenerator();

  const levelItems = Object.values(formContext.watch('levelItems'));
  const isSubmitDisabled = useMemo(() => {
    if (!qBankSelection) {
      return true;
    }

    const isSomeLevelItemsSelected = levelItems.some((value) => value);
    return !isSomeLevelItemsSelected;
  }, [qBankSelection, levelItems]);

  useEffect(() => {
    if (qBankSelection) {
      formContext.reset();
      setQbankStructureId(qBankSelection);
    } else {
      formContext.reset();
      setQbankStructureId('');
    }
  }, [qBankSelection, setQbankStructureId, formContext]);

  const onSubmit = (values: AddQBankToQConfigGeneratorFormValues) => {
    const courseLevelItemId =
      qBankStructureLevelItems.find((item) => item.parentLevelItemId === null)?.id ?? '';
    setSelectedQBankStructure({
      qbankSelection: values.qbankSelection,
      levelItems: { ...values.levelItems, [courseLevelItemId]: true },
    });
    onClose();
  };
  return (
    <FormProvider {...formContext}>
      <Form onSubmit={formContext.handleSubmit(onSubmit)}>
        <DialogContent
          sx={(theme) => ({
            display: 'flex',
            flexDirection: 'column',
            py: theme.spacing(6),
            gap: theme.spacing(6),
            maxHeight: '600px',
            overflow: 'auto',
          })}
        >
          <Flex
            sx={(theme) => ({
              bgcolor: theme.palette.grey[50],
              border: '1px solid',
              borderColor: theme.palette.grey[200],
              borderRadius: theme.shape.rounded(2),
              p: theme.spacing(4),
            })}
          >
            <SearchQBSSelection name='qbankSelection' addedQBSIds={addedQBSIds} />
          </Flex>
          {qBankSelection ? (
            <QBSLevelItemSelectionProvider
              levels={qBankStructureLevels}
              levelItems={qBankStructureLevelItems}
              loading={isLoading}
            >
              <QBSLevelItemSelection />
            </QBSLevelItemSelectionProvider>
          ) : null}
        </DialogContent>
        <Divider />
        <DialogActions>
          <Button variant='outlined' color='secondary' onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => onSubmit(formContext.getValues())} disabled={isSubmitDisabled}>
            Add QBank
          </Button>
        </DialogActions>
      </Form>
    </FormProvider>
  );
}
